import { Activity, Eye, Globe, ShieldCheck, Target, Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export default function TrafficTab(props) {
  const { attest, tab, traffic } = props;
  return (
          <div data-testid="traffic-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mb-6">
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-total-views">
                <Eye size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl">{(traffic?.total_views ?? 0).toLocaleString()}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Total Views</p>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-unique">
                <Users size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl">{(traffic?.unique_total ?? 0).toLocaleString()}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Unique Visitors</p>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-today-views">
                <Activity size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl text-[#D4AF37]">{(traffic?.today_views ?? 0).toLocaleString()}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Views Today</p>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-today-unique">
                <Users size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl">{(traffic?.unique_today ?? 0).toLocaleString()}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Visitors Today</p>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-conversion">
                <Target size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl text-emerald-400">{(traffic?.conversion_rate ?? 0)}%</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Views → Orders</p>
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-attestations">
                <ShieldCheck size={16} color="#D4AF37" className="mb-3" />
                <p className="font-serif text-4xl">{(attest?.total ?? 0).toLocaleString()}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mt-1">Attestations</p>
              </div>
            </div>

            <div className="border border-zinc-800 bg-[#0C0C0C] p-6 mb-6" data-testid="traffic-chart">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-5">Last 14 Days</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={traffic?.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" vertical={false} />
                  <XAxis dataKey="day" stroke="#52525B" fontSize={10} tickLine={false} />
                  <YAxis stroke="#52525B" fontSize={10} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0C0C0C", border: "1px solid #27272A", fontFamily: "monospace", fontSize: 12 }} cursor={{ fill: "rgba(212,175,55,0.06)" }} />
                  <Bar dataKey="views" fill="#D4AF37" name="Views" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="uniques" fill="#3f6212" name="Visitors" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-3 gap-px">
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-top-pages">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-4 flex items-center gap-2"><Eye size={12} /> Top Pages</p>
                {(traffic?.top_pages || []).map((r) => {
                  const max = Math.max(...(traffic.top_pages || []).map((x) => x.count), 1);
                  return (
                    <div key={r.label} className="py-2 border-b border-zinc-900">
                      <div className="flex justify-between mb-1">
                        <span className="font-mono text-xs text-[#A1A1AA] truncate pr-3">{r.label}</span>
                        <span className="font-mono text-xs text-[#F5F5F0] tabular-nums">{r.count}</span>
                      </div>
                      <div className="h-1 bg-zinc-900"><div className="h-1 bg-[#D4AF37]/70" style={{ width: `${Math.max(3, (r.count / max) * 100)}%` }} /></div>
                    </div>
                  );
                })}
                {(!traffic?.top_pages || traffic.top_pages.length === 0) && <p className="font-mono text-sm text-[#52525B] py-3">No data yet.</p>}
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-top-referrers">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-4 flex items-center gap-2"><Globe size={12} /> Top Referrers</p>
                {(traffic?.top_referrers || []).map((r) => {
                  const max = Math.max(...(traffic.top_referrers || []).map((x) => x.count), 1);
                  return (
                    <div key={r.label} className="py-2 border-b border-zinc-900">
                      <div className="flex justify-between mb-1">
                        <span className="font-mono text-xs text-[#A1A1AA] truncate pr-3">{r.label}</span>
                        <span className="font-mono text-xs text-[#F5F5F0] tabular-nums">{r.count}</span>
                      </div>
                      <div className="h-1 bg-zinc-900"><div className="h-1 bg-[#D4AF37]/70" style={{ width: `${Math.max(3, (r.count / max) * 100)}%` }} /></div>
                    </div>
                  );
                })}
                {(!traffic?.top_referrers || traffic.top_referrers.length === 0) && <p className="font-mono text-sm text-[#52525B] py-3">No data yet.</p>}
              </div>
              <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="traffic-top-countries">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] mb-4 flex items-center gap-2"><Globe size={12} /> Top Countries</p>
                {(traffic?.top_countries || []).map((r) => (
                  <div key={r.label} className="flex justify-between py-2 border-b border-zinc-900">
                    <span className="font-mono text-xs text-[#A1A1AA] truncate pr-3">{r.label}</span>
                    <span className="font-mono text-xs text-[#F5F5F0]">{r.count}</span>
                  </div>
                ))}
                {(!traffic?.top_countries || traffic.top_countries.length === 0) && <p className="font-mono text-sm text-[#52525B] py-3">No data yet.</p>}
              </div>
            </div>

            {/* Attestation compliance log */}
            <div className="border border-zinc-800 bg-[#0C0C0C] p-6 mt-6" data-testid="attestation-log">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B] flex items-center gap-2"><ShieldCheck size={12} /> Age-Gate Compliance Log</p>
                <p className="font-mono text-[11px] text-[#52525B]">{attest?.total ?? 0} total · {attest?.today ?? 0} today</p>
              </div>
              <div className="hidden sm:grid grid-cols-12 gap-3 pb-2 border-b border-zinc-800 font-mono text-[9px] uppercase tracking-[0.15em] text-[#52525B]">
                <span className="col-span-5">Timestamp (UTC)</span>
                <span className="col-span-4">Visitor ID</span>
                <span className="col-span-2">Status</span>
                <span className="col-span-1 text-right">Location</span>
              </div>
              {(attest?.recent || []).slice(0, 15).map((a, i) => (
                <div key={`${a.visitor_id}-${a.at}`} data-testid={`attestation-row-${i}`}
                  className="sm:grid sm:grid-cols-12 sm:gap-3 flex flex-col gap-0.5 py-2.5 sm:py-2 border-b border-zinc-900 sm:items-center">
                  <span className="sm:col-span-5 font-mono text-xs text-[#A1A1AA] tabular-nums">{new Date(a.at).toISOString().replace("T", " ").slice(0, 19)} UTC</span>
                  <span className="sm:col-span-4 font-mono text-[11px] text-[#52525B] truncate">{(a.visitor_id || "anonymous").slice(0, 16)}…</span>
                  <span className="sm:col-span-2 font-mono text-[10px] uppercase tracking-[0.1em] text-emerald-400">Passed {a.count ? `· ${a.count}✓` : ""}</span>
                  <span className="sm:col-span-1 sm:text-right font-mono text-[10px] text-[#52525B]">{a.country || "—"}</span>
                </div>
              ))}
              {(!attest?.recent || attest.recent.length === 0) && <p className="font-mono text-sm text-[#52525B] py-3">No attestations recorded yet.</p>}
            </div>
          </div>
  );
}
